import React from 'react'
import _ from 'underscore'
import { CalendarDays, Check, ChevronDown, GraduationCap, Pencil, Plus, User } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import Modal from '../../../../../modal'
import store from '../../../../../../state/store'
import UpdateJobCandidateCommitmentPeriod from '../availability/update_candidate_commitment'
import WeeklyTimeslots from '../../../../../availability/show'
import AvailabilityMatrix from '../../../../../availability_matrix/matrix'
import Timeago from '../../../../../timeago'

class TutorAvailability extends React.Component {

  constructor(props) {
    super(props)
    this.state = {
      modalOpen: false,
      profileModalOpen: false
    }
  }

  openProfileModal = () => this.setState({profileModalOpen: true})
  closeProfileModal = () => this.setState({profileModalOpen: false})

  onAvailableFromChange = (value) => {
    store.emit('tutoring_job:tutor_results:update', {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutor_result.tutoring_job_id,
      available_from: value
    })
  }

  onCommitmentChange = (value) => {
    store.emit('tutoring_job:tutor_results:update', {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutor_result.tutoring_job_id,
      commitment: value
    })
  }

  onSaveTimes = (timeslots) => {
    store.emit('tutoring_job:tutor_results:availability:update', {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutor_result.tutoring_job_id,
      timeslots: timeslots
    })
  }

  openModal = () => this.setState({modalOpen: true})
  closeModal = () => this.setState({modalOpen: false})

  renderAvailabilitySummary(times, emptyMessage = 'No job availability set') {
    if (!times || times.length === 0) {
      return <span className="text-[13px] text-muted-foreground">{emptyMessage}</span>
    }

    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
    const dayShort = {monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'}

    const daySlots = {}
    times.forEach(t => {
      const day = t.day_of_week.toLowerCase()
      if (!daySlots[day]) daySlots[day] = []
      daySlots[day].push(t.start_time.slice(0, 5) + '–' + t.end_time.slice(0, 5))
    })

    const activeDays = dayOrder.filter(d => daySlots[d] && daySlots[d].length > 0)
    if (activeDays.length === 0) {
      return <span className="text-[13px] text-muted-foreground">{emptyMessage}</span>
    }

    const groups = []
    let cur = { days: [activeDays[0]], slots: daySlots[activeDays[0]] }
    for (let i = 1; i < activeDays.length; i++) {
      const prev = activeDays[i - 1]
      const curr = activeDays[i]
      if (JSON.stringify(daySlots[prev]) === JSON.stringify(daySlots[curr]) && dayOrder.indexOf(curr) === dayOrder.indexOf(prev) + 1) {
        cur.days.push(curr)
      } else {
        groups.push(cur)
        cur = { days: [curr], slots: daySlots[curr] }
      }
    }
    groups.push(cur)

    return (
      <div className="rounded-lg border border-[#c6e9d4] bg-[#f0faf4] px-3 py-2.5">
        <div className="mb-2 text-xs font-semibold text-[#276749]">Weekly schedule</div>
        {groups.map((group, i) => {
          const label = group.days.length > 1
            ? dayShort[group.days[0]] + ' – ' + dayShort[group.days[group.days.length - 1]]
            : dayShort[group.days[0]]
          return (
            <div key={i} className="mb-1 flex items-start">
              <span className="mr-2 inline-flex min-w-[70px] shrink-0 items-center justify-center whitespace-nowrap rounded bg-[#38a169] px-2 py-0.5 text-xs font-semibold text-white">{label}</span>
              <div className="flex flex-wrap gap-1">
                {group.slots.map((slot, j) => (
                  <span key={j} className="rounded bg-[#dcf5e7] px-2 py-0.5 text-xs font-medium text-[#276749]">{slot}</span>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  onCommitmentPeriodChange = (value) => {
    store.emit('tutoring_job:tutor_results:update', {
      tutor_id: this.props.tutor_result.tutor.id,
      tutoring_job_id: this.props.tutor_result.tutoring_job_id,
      commitment_period: value
    })
  }

  render() {
    if (!this.props.availability) {
      return (
        <div></div>
      )
    }

    const availableFromOptions = [
      { value: 'today', name: 'Today' },
      { value: 'this_week', name: 'This week' },
      { value: 'next_week', name: 'Next week' }
    ]

    let available_from = 'None Specified'

    if (this.props.tutor_result.job_candidate.available_from === 'today')
      available_from = 'Today'
    else if (this.props.tutor_result.job_candidate.available_from === 'this_week')
      available_from = 'This Week'
    else if (this.props.tutor_result.job_candidate.available_from === 'next_week')
      available_from = 'Next Week'
    else if (this.props.tutor_result.job_candidate.available_from !== '')
      available_from = this.props.tutor_result.job_candidate.available_from

    let commitment = 'None Specified'

    if (this.props.tutor_result.job_candidate.commitment === '0_to_2_weeks')
      commitment = '0 – 2 weeks'
    else if (this.props.tutor_result.job_candidate.commitment === '2_to_4_weeks')
      commitment = '2 weeks – 1 month'
    else if (this.props.tutor_result.job_candidate.commitment === '1_to_3_months')
      commitment = '1 – 3 months'
    else if (this.props.tutor_result.job_candidate.commitment === '3_to_6_months')
      commitment = '3 – 6 months'
    else if (this.props.tutor_result.job_candidate.commitment === 'six_plus_months')
      commitment = '6+ months'

    const hasJobAvailability = this.props.availability.times && this.props.availability.times.length > 0

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="size-4"/>
            Availability
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Job Availability
              </span>
              <Button variant="link" size="sm" className="h-auto p-0 text-[13px]" onClick={this.openProfileModal}>
                <User className="size-3.5"/>View profile availability
              </Button>
            </div>
            {hasJobAvailability && this.props.availability.updated_at &&
              <span className="mb-2 block text-sm text-muted-foreground">Updated <Timeago date={this.props.availability.updated_at}/></span>
            }
            {hasJobAvailability ? (
              <React.Fragment>
                <div onClick={this.openModal} className="cursor-pointer">
                  {this.renderAvailabilitySummary(this.props.availability.times)}
                </div>
                <div className="mt-2">
                  <Button variant="outline" size="sm" onClick={this.openModal}><Pencil className="size-3.5"/>Edit times</Button>
                </div>
              </React.Fragment>
            ) : (
              <div
                onClick={this.openModal}
                className="cursor-pointer rounded-[10px] border border-dashed border-border bg-muted/30 px-4 py-3.5 text-center"
              >
                <div className="mb-2 text-[13px] text-muted-foreground">No job availability set</div>
                <Button size="sm" onClick={this.openModal}>
                  <Plus className="size-3.5"/>Set job availability
                </Button>
              </div>
            )}
            {hasJobAvailability &&
              <div className="mt-4">
                <AvailabilityMatrix
                  availability={this.props.availability}
                  selectedIcon={GraduationCap}
                  otherIcon={User}
                  matchIcon={Check}
                />
              </div>
            }
          </div>
          <div className="grid content-start gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <span className="shrink-0 text-sm font-medium">Available From:</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">{available_from} <ChevronDown className="size-3.5"/></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {_.map(availableFromOptions, (option) => {
                      const pressed = (option.value === this.props.tutor_result.job_candidate.available_from)

                      return (
                        <DropdownMenuItem
                          key={option.value}
                          className={cn(pressed && 'bg-muted')}
                          onSelect={() => this.onAvailableFromChange(option.value)}
                        >
                          {option.name}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="shrink-0 text-sm font-medium">Can Commit Until:</span>
                <UpdateJobCandidateCommitmentPeriod
                  key={this.props.tutor_result.job_candidate.commitment_period}
                  commitment_period={this.props.tutor_result.job_candidate.commitment_period}
                  onChange={this.onCommitmentPeriodChange}
                />
              </div>
            </div>
            {hasJobAvailability &&
              <div className="grid gap-3">
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-[50px] shrink-0 items-center justify-center rounded bg-[#3ecf8e]"><Check className="size-5 text-white"/></span>
                  <span>Matched Availability</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-[50px] shrink-0 items-center justify-center rounded bg-[#68d4f8]"><GraduationCap className="size-5 text-white"/></span>
                  <span>Tutor's Availability</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="flex h-12 w-[50px] shrink-0 items-center justify-center rounded bg-[#7795f8]"><User className="size-5 text-white"/></span>
                  <span>Learner's Availability</span>
                </div>
              </div>
            }
          </div>
        </CardContent>
        {this.state.modalOpen &&
          <Modal title={`Edit ${this.props.tutor_result.tutor.name}'s Job Availability`} size="modal-lg" onClose={this.closeModal}>
            <div className="p-6">
              <p className="mb-4 text-[13px] text-muted-foreground">
                These times apply only to this job. The tutor's general profile availability is not affected.
              </p>
              <WeeklyTimeslots
                times={this.props.availability.times}
                onSaveTimes={this.onSaveTimes}
              />
            </div>
          </Modal>
        }
        {this.state.profileModalOpen &&
          <Modal title={`${this.props.tutor_result.tutor.name}'s Profile Availability`} size="modal-md" onClose={this.closeProfileModal}>
            <div className="p-6">
              <p className="mb-4 text-[13px] text-muted-foreground">
                The tutor's general weekly availability from their profile. This is not their commitment for this job — use "Edit times" to set job-specific availability.
              </p>
              {this.renderAvailabilitySummary(this.props.profile_availability && this.props.profile_availability.times, 'No profile availability set')}
            </div>
          </Modal>
        }
      </Card>
    )
  }
}

export default TutorAvailability
